import { useCallback, useState } from 'react'
import type { SessionTakeoverOptions } from '@/lib/api/services/sessions'

interface OpenForSessionParams {
  sessionId: string
  options?: SessionTakeoverOptions
}

interface UseTakeoverTeamModalResult {
  isOpen: boolean
  selectedTeamId: number | null
  pendingSessionId: string | null
  pendingOptions?: SessionTakeoverOptions
  pendingAcceptRequestId: number | null
  isSubmitting: boolean
  openForSession: (params: OpenForSessionParams) => void
  openForAccept: (sessionId: string, requestId: number) => void
  close: () => void
  setSelectedTeamId: (teamId: number | null) => void
  setPendingOptions: (options: SessionTakeoverOptions | undefined) => void
  setPendingAcceptRequestId: (requestId: number | null) => void
  setSubmitting: (value: boolean) => void
}

export function useTakeoverTeamModal(): UseTakeoverTeamModalResult {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null)
  const [pendingSessionId, setPendingSessionId] = useState<string | null>(null)
  const [pendingOptions, setPendingOptions] = useState<
    SessionTakeoverOptions | undefined
  >(undefined)
  const [pendingAcceptRequestId, setPendingAcceptRequestId] = useState<
    number | null
  >(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const resetState = useCallback(() => {
    setIsOpen(false)
    setSelectedTeamId(null)
    setPendingSessionId(null)
    setPendingOptions(undefined)
    setPendingAcceptRequestId(null)
    setIsSubmitting(false)
  }, [])

  const openForSession = useCallback(
    ({ sessionId, options }: OpenForSessionParams) => {
      setPendingSessionId(sessionId)
      setPendingOptions(options)
      setPendingAcceptRequestId(null)
      setSelectedTeamId(null)
      setIsOpen(true)
    },
    []
  )

  const openForAccept = useCallback((sessionId: string, requestId: number) => {
    setPendingSessionId(sessionId)
    setPendingAcceptRequestId(requestId)
    setPendingOptions(undefined)
    setSelectedTeamId(null)
    setIsOpen(true)
  }, [])

  const close = useCallback(() => {
    resetState()
  }, [resetState])

  const handleSetSelectedTeamId = useCallback((teamId: number | null) => {
    setSelectedTeamId(teamId)
  }, [])

  const handleSetPendingOptions = useCallback(
    (options: SessionTakeoverOptions | undefined) => {
      setPendingOptions(options)
    },
    []
  )

  const handleSetPendingAcceptRequestId = useCallback(
    (requestId: number | null) => {
      setPendingAcceptRequestId(requestId)
    },
    []
  )

  const handleSetSubmitting = useCallback((value: boolean) => {
    setIsSubmitting(value)
  }, [])

  return {
    isOpen,
    selectedTeamId,
    pendingSessionId,
    pendingOptions,
    pendingAcceptRequestId,
    isSubmitting,
    openForSession,
    openForAccept,
    close,
    setSelectedTeamId: handleSetSelectedTeamId,
    setPendingOptions: handleSetPendingOptions,
    setPendingAcceptRequestId: handleSetPendingAcceptRequestId,
    setSubmitting: handleSetSubmitting,
  }
}
